import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Filter, MapPin, ArrowRight, Construction, Heart } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { projectsData, infrastructureProjectsData, socialProjectsData } from '../data/mock';

const ProjectsPage = () => {
  const { t, language } = useLanguage();
  const [filter, setFilter] = useState('all');

  // Scroll reveal animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.scroll-reveal, .scroll-reveal-left, .scroll-reveal-right, .scroll-reveal-scale').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const filteredProjects = projectsData.filter(project => 
    filter === 'all' ? true : project.status === filter
  );

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black z-10" />
        <img
          src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzY4NjMyMDc5fDA&ixlib=rb-4.1.0&q=85"
          alt="Projects"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative z-20 text-center px-6 max-w-4xl mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-6 font-light animate-fade-in-up">
            {language === 'en' ? 'OUR PORTFOLIO' : language === 'ar' ? 'أعمالنا' : 'پۆرتفۆلیۆکەمان'}
          </p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight mb-6 animate-fade-in-up delay-200">
            {language === 'en' ? 'Our Projects' : language === 'ar' ? 'مشاريعنا' : 'پڕۆژەکانمان'}
          </h1>
          <p className="text-lg text-white/70 font-light animate-fade-in-up delay-300">
            {language === 'en' ? 'Discover our premium residential developments' : language === 'ar' ? 'اكتشف مشاريعنا السكنية المتميزة' : 'پڕۆژە نیشتەجێبوونی پریمیەمەکانمان بدۆزەرەوە'}
          </p>
        </div>
      </section>

      {/* Residential Projects Section */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          {/* Section Header */}
          <div className="text-center mb-16 scroll-reveal">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4 font-light">
              {language === 'en' ? 'RESIDENTIAL' : language === 'ar' ? 'سكني' : 'نیشتەجێبوون'}
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
              {language === 'en' ? 'Residential Projects' : language === 'ar' ? 'المشاريع السكنية' : 'پڕۆژە نیشتەجێبوونەکان'}
            </h2>
          </div>

          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-3 mb-16 scroll-reveal">
            {[
              { id: 'all', label: language === 'en' ? 'All' : language === 'ar' ? 'الكل' : 'هەموو' },
              { id: 'completed', label: t('projectDetail.completed') },
              { id: 'ongoing', label: t('projectDetail.ongoing') }
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => setFilter(item.id)}
                className={`px-8 py-3 text-xs uppercase tracking-[0.15em] font-light border transition-all duration-300 ${
                  filter === item.id
                    ? 'bg-white text-black border-white'
                    : 'bg-transparent text-white/60 border-white/20 hover:border-white/50 hover:text-white'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Projects Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project, index) => (
              <Link
                key={project.id}
                to={`/projects/${project.id}`}
                className="group relative h-[550px] overflow-hidden image-zoom-container scroll-reveal-scale tilt-3d"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`project-card-${project.id}`}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent z-10 transition-all duration-500 group-hover:via-black/60" />
                <img
                  src={project.images[0]}
                  alt={project.name[language] || project.name.en}
                  className="w-full h-full object-cover image-zoom"
                />
                
                <div className="absolute inset-0 z-20 flex flex-col justify-end p-8">
                  {/* Project Logo */}
                  <div className="flex justify-center mb-6">
                    <img
                      src={project.logo}
                      alt={project.name[language] || project.name.en}
                      className={`object-contain transition-transform duration-500 group-hover:scale-110 drop-shadow-[0_0_15px_rgba(255,255,255,0.25)] ${
                        (project.id === 'nawzhen_city' || project.id === 'dream_land')
                          ? 'h-20 w-20'
                          : 'h-24 max-w-[220px]'
                      }`}
                    />
                  </div>
                  
                  {/* Location */}
                  <p className="text-xs uppercase tracking-[0.2em] text-white/50 font-light mb-4">
                    {project.location}
                  </p>
                  
                  {/* Status Badge */}
                  <div className="mb-6">
                    <span className={`inline-block px-4 py-1.5 text-[10px] uppercase tracking-widest font-light ${
                      project.status === 'completed' 
                        ? 'bg-white text-black' 
                        : 'bg-white/10 text-white border border-white/30'
                    }`}>
                      {project.status === 'completed' ? t('projectDetail.completed') : t('projectDetail.ongoing')}
                    </span>
                  </div>

                  {/* View Details Link */}
                  <div className="flex items-center gap-3 text-white text-xs uppercase tracking-[0.15em] font-light opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-500">
                    <span className="border-b border-white/50 pb-1">{t('featuredProjects.viewDetails')}</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-32 scroll-reveal">
              <Filter className="w-16 h-16 mx-auto text-white/20 mb-6" />
              <p className="text-lg text-white/40 font-light">No projects found for this filter</p>
            </div>
          )}
        </div>
      </section>

      {/* Infrastructure Projects Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          {/* Section Header */}
          <div className="text-center mb-16 scroll-reveal">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 mb-6">
              <Construction className="w-4 h-4 text-white/60" />
              <span className="text-xs uppercase tracking-widest font-light text-white/60">
                {t('infrastructure.sectionTitle')}
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">
              {t('infrastructure.sectionTitle')}
            </h2>
            <p className="text-white/50 font-light max-w-2xl mx-auto">
              {t('infrastructure.sectionSubtitle')}
            </p>
          </div>

          {/* Infrastructure Projects Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {infrastructureProjectsData.map((project, index) => (
              <Link
                key={project.id}
                to={`/infrastructure/${project.id}`}
                className="group relative overflow-hidden scroll-reveal-scale"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`infrastructure-card-${project.id}`}
              >
                {/* Image Container */}
                <div className="relative h-80 overflow-hidden image-zoom-container">
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent z-10" />
                  <img
                    src={project.heroImage}
                    alt={project.name[language] || project.name.en}
                    className="w-full h-full object-cover image-zoom"
                  />
                  {/* Status Badge */}
                  <div className="absolute top-4 left-4 z-20">
                    <span className="px-4 py-1.5 text-[10px] uppercase tracking-widest font-light bg-white text-black">
                      {t('projectDetail.completed')}
                    </span>
                  </div>
                  {/* Project Type Badge */}
                  <div className="absolute top-4 right-4 z-20">
                    <span className="px-4 py-1.5 text-[10px] uppercase tracking-widest font-light bg-white/10 text-white border border-white/20 flex items-center gap-2">
                      <Construction className="w-3 h-3" />
                      {t('services.roads.title')}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-8 bg-[#111] border border-white/10 group-hover:border-white/20 transition-colors">
                  <h3 className="text-xl sm:text-2xl font-light text-white mb-4 tracking-wide group-hover:text-white transition-colors">
                    {project.name[language] || project.name.en}
                  </h3>
                  
                  {/* Location */}
                  <div className="flex items-center gap-2 text-white/40 mb-4">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm font-light">
                      {project.location[language] || project.location.en}
                    </span>
                  </div>

                  {/* Overview Excerpt */}
                  <p className="text-white/50 text-sm leading-relaxed mb-6 line-clamp-2">
                    {project.overview[language] || project.overview.en}
                  </p>

                  {/* CTA */}
                  <div className="flex items-center gap-3 text-white text-xs uppercase tracking-[0.15em] font-light">
                    <span className="border-b border-white/30 pb-1 group-hover:border-white/60 transition-colors">
                      {t('infrastructure.viewProject')}
                    </span>
                    <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Social Projects Section */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          {/* Section Header */}
          <div className="text-center mb-16 scroll-reveal">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 mb-6">
              <Heart className="w-4 h-4 text-white/60" />
              <span className="text-xs uppercase tracking-widest font-light text-white/60">
                {t('social.sectionTitle')}
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">
              {t('social.sectionTitle')}
            </h2>
            <p className="text-white/50 font-light max-w-2xl mx-auto">
              {t('social.sectionSubtitle')}
            </p>
          </div>

          {/* Social Projects Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {socialProjectsData.map((project, index) => (
              <Link
                key={project.id}
                to={`/social/${project.id}`}
                className="group relative overflow-hidden scroll-reveal-scale"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`social-card-${project.id}`}
              >
                {/* Image Container */}
                <div className="relative h-80 overflow-hidden image-zoom-container">
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent z-10" />
                  <img
                    src={project.heroImage}
                    alt={project.name[language] || project.name.en}
                    className="w-full h-full object-cover image-zoom"
                  />
                  {/* Status Badge */}
                  <div className="absolute top-4 left-4 z-20">
                    <span className="px-4 py-1.5 text-[10px] uppercase tracking-widest font-light bg-white text-black">
                      {t('social.completed')}
                    </span>
                  </div>
                  {/* Project Type Badge */}
                  <div className="absolute top-4 right-4 z-20">
                    <span className="px-4 py-1.5 text-[10px] uppercase tracking-widest font-light bg-white/10 text-white border border-white/20 flex items-center gap-2">
                      <Heart className="w-3 h-3" />
                      {language === 'en' ? 'Social Care' : language === 'ar' ? 'رعاية اجتماعية' : 'چاودێری کۆمەڵایەتی'}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-8 bg-[#111] border border-white/10 group-hover:border-white/20 transition-colors">
                  <h3 className="text-xl sm:text-2xl font-light text-white mb-4 tracking-wide group-hover:text-white transition-colors">
                    {project.name[language] || project.name.en}
                  </h3>
                  
                  {/* Location */}
                  <div className="flex items-center gap-2 text-white/40 mb-4">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm font-light">
                      {project.location[language] || project.location.en}
                    </span>
                  </div>

                  {/* Overview Excerpt */}
                  <p className="text-white/50 text-sm leading-relaxed mb-6 line-clamp-2">
                    {project.overview[language] || project.overview.en}
                  </p>

                  {/* CTA */}
                  <div className="flex items-center gap-3 text-white text-xs uppercase tracking-[0.15em] font-light">
                    <span className="border-b border-white/30 pb-1 group-hover:border-white/60 transition-colors">
                      {t('social.viewProject')}
                    </span>
                    <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectsPage;
